import { useCallback } from 'react';
import { useQueryClient } from 'react-query';

import { type IConversation } from 'src/api/landlord-messages-api';
import { type IUser } from 'src/api/users-api';
import { queryKeys } from 'src/services/api/constants';
import { type ILead } from 'src/services/api/leads/types';

export const useUpdateConversations = () => {
    const queryClient = useQueryClient();

    const updateConversations = useCallback((updatedConversations: IConversation[]) => {
        const loggedInUser = queryClient.getQueryData<IUser>(queryKeys.loggedInUser);
        const leads = queryClient.getQueryData<ILead[]>(queryKeys.leads);

        if (!loggedInUser || !leads) {
            return;
        }

        updatedConversations.forEach((updatedConversation) => {
            queryClient.setQueryData([
                queryKeys.conversations,
                'lead',
                updatedConversation.lead_uuid,
            ], updatedConversation);
        });

        // Keep both cache projections in sync: thread readers use the per-lead
        // key above, while inbox readers subscribe to the ordered list.
        queryClient.setQueryData(queryKeys.conversations, (oldData: IConversation[] | undefined) => {
            if (!oldData) {
                return updatedConversations;
            }
            const updatedConversationsByLeadUuid = new Map(updatedConversations.map((o) => [o.lead_uuid, o]));
            const filtered = oldData.filter((conv) => !updatedConversationsByLeadUuid.has(conv.lead_uuid));
            const merged = [...filtered, ...updatedConversationsByLeadUuid.values()];
            return merged.sort((a, b) => {
                return new Date(b.last_contact_at).getTime() - new Date(a.last_contact_at).getTime();
            });
        });
    }, [queryClient]);

    return updateConversations;
};
